/**
 * Core Task Migration API Client (Epic 6 — Migratie & livegang, Story 6.3)
 *
 * Eigen bestand naast `coreTasksApi.ts` (Story 2.2's catalogus-client) —
 * dezelfde rationale als die story: migratie is een eigen, tijdelijk domein
 * (zie Story 6.3 Dev Notes: "dit scherm is tijdelijk"). Consumeert de
 * Story 6.1/6.2/6.3-endpoints onder `/core-tasks/migration*` en
 * `/core-tasks/impact-report*`.
 */

import { apiRequest, getAccessToken } from './api';
import type {
  MigrationDryRunResult,
  MigrationCommitResult,
  ImpactReportResult,
  MigrationStatusResult,
} from '@functieloonhuis/shared';

interface DataResponse<T> {
  data: T;
}

export const coreTaskMigrationApi = {
  status: async (): Promise<MigrationStatusResult> => {
    const res = await apiRequest<DataResponse<MigrationStatusResult>>('/core-tasks/migration/status');
    return res.data;
  },

  dryRun: async (migrationMoment: string): Promise<MigrationDryRunResult> => {
    const res = await apiRequest<DataResponse<MigrationDryRunResult>>('/core-tasks/migration', {
      method: 'POST',
      body: JSON.stringify({ dryRun: true, migrationMoment }),
    });
    return res.data;
  },

  commit: async (migrationMoment: string): Promise<MigrationCommitResult> => {
    const res = await apiRequest<DataResponse<MigrationCommitResult>>('/core-tasks/migration', {
      method: 'POST',
      body: JSON.stringify({ dryRun: false, migrationMoment }),
    });
    return res.data;
  },

  deactivate: async (): Promise<MigrationStatusResult> => {
    await apiRequest('/core-tasks/migration/deactivate', { method: 'POST' });
    return coreTaskMigrationApi.status();
  },

  impactReport: async (): Promise<ImpactReportResult> => {
    const res = await apiRequest<DataResponse<ImpactReportResult>>('/core-tasks/impact-report');
    return res.data;
  },

  reviewImpactReport: async (name: string): Promise<MigrationStatusResult> => {
    const res = await apiRequest<DataResponse<MigrationStatusResult>>('/core-tasks/impact-report/review', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    return res.data;
  },

  publishManual: async (name: string): Promise<MigrationStatusResult> => {
    const res = await apiRequest<DataResponse<MigrationStatusResult>>('/core-tasks/manual-published', {
      method: 'POST',
      body: JSON.stringify({ name }),
    });
    return res.data;
  },

  exportImpactReportCsv: async (): Promise<Blob> => {
    const response = await fetch('/api/v1/core-tasks/impact-report/export', {
      method: 'GET',
      headers: { Authorization: `Bearer ${getAccessToken() ?? ''}` },
    });
    if (!response.ok) {
      throw new Error('Export impactrapport mislukt');
    }
    return response.blob();
  },
};
